import { useState, useEffect, useCallback } from 'react'
import { fetchStudentLogs } from '../api/studentLogsApi'
import { appToast } from '../../../../components/AppToast'

function formatDate(d) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function calculatePeriodDates(periodVal) {
  const today = new Date()
  const now = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  switch (String(periodVal)) {
    case '1':
    case 'Current Year': {
      const from = new Date(now.getFullYear(), 0, 1)
      const till = new Date(now.getFullYear(), 11, 31)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '2':
    case 'Current Month': {
      const from = new Date(now.getFullYear(), now.getMonth(), 1)
      const till = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '3':
    case 'Current Week': {
      const day = now.getDay()
      const diff = now.getDate() - day + (day === 0 ? -6 : 1) // Monday
      const from = new Date(now.setDate(diff))
      const till = new Date(from)
      till.setDate(from.getDate() + 6)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '4':
    case 'Last Year': {
      const from = new Date(now.getFullYear() - 1, 0, 1)
      const till = new Date(now.getFullYear() - 1, 11, 31)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '5':
    case 'Last Month': {
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const till = new Date(now.getFullYear(), now.getMonth(), 0)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '6':
    case 'Last Week': {
      const day = now.getDay()
      const diff = now.getDate() - day - 6 + (day === 0 ? -6 : 1)
      const from = new Date(now.setDate(diff))
      const till = new Date(from)
      till.setDate(from.getDate() + 6)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '7':
    case 'Next Year': {
      const from = new Date(now.getFullYear() + 1, 0, 1)
      const till = new Date(now.getFullYear() + 1, 11, 31)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '8':
    case 'Next Month': {
      const from = new Date(now.getFullYear(), now.getMonth() + 1, 1)
      const till = new Date(now.getFullYear(), now.getMonth() + 2, 0)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '9':
    case 'Next Week': {
      const day = now.getDay()
      const diff = now.getDate() - day + 8 + (day === 0 ? -6 : 1)
      const from = new Date(now.setDate(diff))
      const till = new Date(from)
      till.setDate(from.getDate() + 6)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '12':
    case 'Today': {
      return { fromDate: formatDate(now), tillDate: formatDate(now) }
    }
    case '13':
    case 'Yesterday': {
      const y = new Date(now)
      y.setDate(y.getDate() - 1)
      return { fromDate: formatDate(y), tillDate: formatDate(y) }
    }
    default:
      return { fromDate: '', tillDate: '' }
  }
}

export default function useStudentLogs() {
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(false)
  const [totalCount, setTotalCount] = useState(0)

  // Filters
  const [moduleFilter, setModuleFilter] = useState('All')
  const [periodFilter, setPeriodFilterState] = useState('0')
  const [fromDate, setFromDate] = useState('')
  const [tillDate, setTillDate] = useState('')
  const [searchText, setSearchText] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const handlePeriodChange = (val) => {
    setPeriodFilterState(val)
    const { fromDate: fDate, tillDate: tDate } = calculatePeriodDates(val)
    if (fDate || tDate) {
      setFromDate(fDate)
      setTillDate(tDate)
    }
  }

  useEffect(() => {
    setPage(1)
  }, [moduleFilter, fromDate, tillDate, searchText])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchStudentLogs({
        module: moduleFilter,
        fromDate,
        tillDate,
        name: searchText,
      })
      setRecords(res.records)
      setTotalCount(res.totalRecords)
    } catch (e) {
      console.error('Failed to load student logs', e)
      appToast.error('Failed to load student activity logs.')
    } finally {
      setLoading(false)
    }
  }, [moduleFilter, fromDate, tillDate, searchText])

  useEffect(() => {
    loadData()
  }, [loadData])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const pagedRecords = records.slice((page - 1) * pageSize, page * pageSize)
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeEnd = Math.min(totalCount, page * pageSize)

  const goToNextPage = () => {
    setPage((prev) => Math.min(totalPages, prev + 1))
  }

  const goToPreviousPage = () => {
    setPage((prev) => Math.max(1, prev - 1))
  }

  const handleResetFilters = () => {
    setModuleFilter('All')
    setPeriodFilterState('0')
    setFromDate('')
    setTillDate('')
    setSearchText('')
  }

  const options = {
    modules: [
      { value: 'All', label: 'Select Modules (All)' },
      { value: 'Test performance', label: 'Test performance' },
      { value: 'Photo Gallery', label: 'Photo Gallery' },
      { value: 'Video', label: 'Video' },
      { value: 'Feedback', label: 'Feedback' },
      { value: 'Lecture', label: 'Lecture' },
      { value: 'Test', label: 'Test' },
      { value: 'Login', label: 'Login' },
      { value: 'Fee', label: 'Fee' },
      { value: 'Biometric', label: 'Biometric' },
      { value: 'Attendance', label: 'Attendance' },
      { value: 'Solutions', label: 'Solutions' },
      { value: 'Events', label: 'Events' },
      { value: 'Remarks', label: 'Remarks' },
      { value: 'Notice Board', label: 'Notice Board' },
      { value: 'Test Attendance', label: 'Test Attendance' },
      { value: 'Logout', label: 'Logout' },
      { value: 'Message', label: 'Message' },
      { value: 'Syllabus', label: 'Syllabus' },
    ],
    periods: [
      { value: '0', label: 'Period (All)' },
      { value: '1', label: 'Current Year' },
      { value: '2', label: 'Current Month' },
      { value: '3', label: 'Current Week' },
      { value: '4', label: 'Last Year' },
      { value: '5', label: 'Last Month' },
      { value: '6', label: 'Last Week' },
      { value: '7', label: 'Next Year' },
      { value: '8', label: 'Next Month' },
      { value: '9', label: 'Next Week' },
      { value: '12', label: 'Today' },
      { value: '13', label: 'Yesterday' },
    ],
  }

  return {
    state: {
      records,
      pagedRecords,
      loading,
      totalCount,
      moduleFilter,
      periodFilter,
      fromDate,
      tillDate,
      searchText,
      options,
      page,
      pageSize,
      totalPages,
      rangeStart,
      rangeEnd,
    },
    actions: {
      setModuleFilter,
      setPeriodFilter: handlePeriodChange,
      setFromDate,
      setTillDate,
      setSearchText,
      setPage,
      setPageSize,
      goToNextPage,
      goToPreviousPage,
      handleResetFilters,
      loadData,
    },
  }
}
